/**
 * Pure helpers for decommission fly-away animation triggering.
 *
 * Triggers are scoped to a specific play (new strategem 111 instance or new
 * deploy decommission providedOrders entry) so animations do not replay on
 * unrelated state updates.
 */

const KINETRON_BLAST_ORDER_ID = 111;
const KINETRON_SIEGE_TANK_ORDER_ID = 82;
const HUMAN_TACTICAL_SUPPORT_ORDER_ID = 354;

/** Deploy units that decommission via addCardToHand + providedBy metadata */
const DECOMMISSION_DEPLOY_PROVIDER_IDS = [
  KINETRON_SIEGE_TANK_ORDER_ID,
  HUMAN_TACTICAL_SUPPORT_ORDER_ID
];

function collectKinetronBlastInstanceIds(gameState) {
  if (!gameState || !gameState.state) {
    throw new Error('collectKinetronBlastInstanceIds: gameState.state is required');
  }

  const allPlayerIds = gameState.players || Object.keys(gameState.state || {});
  const instanceIds = new Set();

  allPlayerIds.forEach(playerId => {
    const player = gameState.state?.[playerId];
    if (!player || !Array.isArray(player.completedOrders)) return;

    player.completedOrders.forEach(order => {
      if (!order) return;
      if (order.id === KINETRON_BLAST_ORDER_ID && (order.type === 'Strategem' || !order.type) && order.instanceId) {
        instanceIds.add(order.instanceId);
      }
    });
  });

  return instanceIds;
}

/**
 * Deploy decommissions (Siege Tank 82, Human Tactical Support 354, etc.) record
 * returned units in providedOrders via addCardToHand(..., providedBy).
 */
function collectDeployDecommissionProvidedOrderKeys(gameState) {
  if (!gameState || !gameState.state) {
    throw new Error('collectDeployDecommissionProvidedOrderKeys: gameState.state is required');
  }

  const allPlayerIds = gameState.players || Object.keys(gameState.state || {});
  const keys = new Set();

  allPlayerIds.forEach(playerId => {
    const player = gameState.state?.[playerId];
    if (!player || !Array.isArray(player.providedOrders)) return;

    player.providedOrders.forEach(entry => {
      if (!entry?.orderInstanceId) return;
      const providerId = entry.providedBy?.id;
      if (
        DECOMMISSION_DEPLOY_PROVIDER_IDS.includes(providerId) &&
        entry.providedBy?.type === 'unit'
      ) {
        keys.add(`deploy:${providerId}:${entry.orderInstanceId}`);
      }
    });
  });

  return keys;
}

/** @deprecated Use collectDeployDecommissionProvidedOrderKeys — siege-only keys for legacy tests */
function collectKinetronSiegeTankDecommissionKeys(gameState) {
  const keys = new Set();
  collectDeployDecommissionProvidedOrderKeys(gameState).forEach(key => {
    if (key.startsWith(`deploy:${KINETRON_SIEGE_TANK_ORDER_ID}:`)) {
      keys.add(key.slice(`deploy:${KINETRON_SIEGE_TANK_ORDER_ID}:`.length));
    }
  });
  return keys;
}

function collectDecommissionAnimationTriggerKeys(gameState) {
  const keys = new Set();

  collectKinetronBlastInstanceIds(gameState).forEach(instanceId => {
    keys.add(`blast:${instanceId}`);
  });

  collectDeployDecommissionProvidedOrderKeys(gameState).forEach(key => {
    keys.add(key);
  });

  return keys;
}

function hasNewKinetronBlastInstance(previousInstanceIds, currentInstanceIds) {
  if (!(previousInstanceIds instanceof Set)) {
    throw new Error('hasNewKinetronBlastInstance: previousInstanceIds must be a Set');
  }
  if (!(currentInstanceIds instanceof Set)) {
    throw new Error('hasNewKinetronBlastInstance: currentInstanceIds must be a Set');
  }

  for (const instanceId of currentInstanceIds) {
    if (!previousInstanceIds.has(instanceId)) {
      return true;
    }
  }
  return false;
}

function hasNewDecommissionAnimationTrigger(previousKeys, currentKeys) {
  if (!(previousKeys instanceof Set)) {
    throw new Error('hasNewDecommissionAnimationTrigger: previousKeys must be a Set');
  }
  if (!(currentKeys instanceof Set)) {
    throw new Error('hasNewDecommissionAnimationTrigger: currentKeys must be a Set');
  }

  for (const key of currentKeys) {
    if (!previousKeys.has(key)) {
      return true;
    }
  }
  return false;
}

/**
 * Whether a unit that left the battlefield was returned to hand by decommission.
 * Kinetron Blast issues a fresh instanceId; Siege Tank keeps the battlefield instanceId.
 */
function isDecommissionedUnitInHand(hand, previousUnit) {
  if (!Array.isArray(hand) || !previousUnit?.instanceId) {
    return false;
  }

  return hand.some(order => {
    if (!order) return false;
    if (order.instanceId === previousUnit.instanceId) {
      return true;
    }
    return order.id === previousUnit.id && order.instanceId !== previousUnit.instanceId;
  });
}

export {
  KINETRON_BLAST_ORDER_ID,
  KINETRON_SIEGE_TANK_ORDER_ID,
  HUMAN_TACTICAL_SUPPORT_ORDER_ID,
  DECOMMISSION_DEPLOY_PROVIDER_IDS,
  collectKinetronBlastInstanceIds,
  collectDeployDecommissionProvidedOrderKeys,
  collectKinetronSiegeTankDecommissionKeys,
  collectDecommissionAnimationTriggerKeys,
  hasNewKinetronBlastInstance,
  hasNewDecommissionAnimationTrigger,
  isDecommissionedUnitInHand,
};

export default {
  KINETRON_BLAST_ORDER_ID,
  KINETRON_SIEGE_TANK_ORDER_ID,
  HUMAN_TACTICAL_SUPPORT_ORDER_ID,
  DECOMMISSION_DEPLOY_PROVIDER_IDS,
  collectKinetronBlastInstanceIds,
  collectDeployDecommissionProvidedOrderKeys,
  collectKinetronSiegeTankDecommissionKeys,
  collectDecommissionAnimationTriggerKeys,
  hasNewKinetronBlastInstance,
  hasNewDecommissionAnimationTrigger,
  isDecommissionedUnitInHand,
};
